import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

export default function AmbientLightingSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const [selectedColor, setSelectedColor] = useState('#10b981');

  const colors = [
    { name: 'Groen', value: '#10b981', prompt: 'green', url: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/29c08486e293a48dd79e569bc7e1e6f1.png' },
    { name: 'Paars', value: '#9333ea', prompt: 'purple', url: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/fecc6cd4597fcfc58cb2d693261a557f.png' },
    { name: 'Roze', value: '#ec4899', prompt: 'pink', url: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/27fad7e1f4a5fefac9b10d2b84a207a7.png' },
    { name: 'Oranje', value: '#f97316', prompt: 'orange', url: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/3eb204dc61e8e7eb563f4d476b2d5dab.png' },
    { name: 'Rood', value: '#ef4444', prompt: 'red', url: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/b797542716c9c8dfdc41b410f3f237f8.png' },
    { name: 'Blauw', value: '#3b82f6', prompt: 'blue', url: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/2a86b8b4b9f1dc4b3f76a4b11ee3fdc2.png' },
  ];

  const [currentColorPrompt, setCurrentColorPrompt] = useState('green');
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  // Preload all images before showing anything
  useEffect(() => {
    const preloadPromises = colors.map((color) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = color.url;
      });
    });

    Promise.all(preloadPromises).then(() => {
      setImagesPreloaded(true);
    });
  }, []);

  const handleColorChange = (color: typeof colors[0]) => {
    setSelectedColor(color.value);
    setCurrentColorPrompt(color.prompt);
  };

  // Get image URL based on selected color
  const getImageUrl = () => {
    const color = colors.find(c => c.prompt === currentColorPrompt);
    return color?.url || colors[0].url;
  };

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center py-16 md:py-32 bg-black">
      <motion.div 
        style={{ opacity }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"
      >
        <div className="grid lg:grid-cols-2 gap-10 md:gap-20 items-center">
          {/* Left - LED Demo with Real Car Photo */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative aspect-[4/3] bg-black rounded-2xl md:rounded-3xl overflow-hidden border-2 md:border-4 border-pink-500/30 shadow-2xl shadow-pink-500/20">
              {/* All images stacked, only show the selected one */}
              {imagesPreloaded && colors.map((color) => (
                <img
                  key={color.prompt}
                  src={color.url}
                  alt={`Auto interieur met ${color.name} LED verlichting`}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                  style={{ 
                    opacity: currentColorPrompt === color.prompt ? 1 : 0,
                    pointerEvents: currentColorPrompt === color.prompt ? 'auto' : 'none'
                  }}
                />
              ))}

              {/* Loading state */}
              {!imagesPreloaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin"></div>
                </div>
              )}

              {/* Color Selector Palette */}
              <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3 bg-black/80 backdrop-blur-md p-3 md:p-4 rounded-2xl border border-white/20 z-10">
                {colors.map((color) => (
                  <motion.button
                    key={color.value}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleColorChange(color)}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full border-3 cursor-pointer transition-all duration-300"
                    style={{
                      backgroundColor: color.value,
                      borderColor: selectedColor === color.value ? '#ffffff' : 'transparent',
                      boxShadow: selectedColor === color.value ? `0 0 20px ${color.value}` : 'none',
                    }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Outer Glow Effect */}
            <motion.div
              animate={{ backgroundColor: `${selectedColor}20` }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 blur-3xl -z-10"
            />
          </motion.div>

          {/* Right Content */}
          <div className="space-y-6 md:space-y-10 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-8" style={{ fontFamily: 'Orbitron, monospace' }}>
                <span className="bg-gradient-to-r from-pink-400 to-orange-500 bg-clip-text text-transparent">
                  Sfeerverlichting
                </span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-400 leading-relaxed mb-6 md:mb-10">
                Creëer de perfecte sfeer met LED verlichting voor interieur, 
                deurpanelen, voetenruimte en koffer. Volledig aanpasbaar aan uw wensen.
              </p>
            </motion.div>

            {/* Features */}
            <div className="space-y-4 md:space-y-8">
              {[
                { icon: 'ri-lightbulb-line', title: 'RGB LED Strips', desc: 'Miljoenen kleuren beschikbaar' },
                { icon: 'ri-remote-control-line', title: 'App Bediening', desc: 'Bedien via smartphone of dashboard' },
                { icon: 'ri-music-2-line', title: 'Muziek Sync', desc: 'Lichtshow op het ritme van muziek' },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3 md:gap-6"
                >
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-pink-500/20 to-orange-500/20 rounded-lg md:rounded-xl border border-pink-500/30 flex-shrink-0">
                    <i className={`${feature.icon} text-2xl md:text-3xl text-pink-400`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-semibold text-white mb-1 md:mb-3">{feature.title}</h3>
                    <p className="text-gray-400 text-sm md:text-lg">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.REACT_APP_NAVIGATE('/diensten/sterrenhemel-sfeerverlichting-achteruitcamera-in-antwerpen')}
              className="mt-6 md:mt-12 w-full sm:w-auto px-6 md:px-10 py-4 md:py-5 bg-gradient-to-r from-pink-500 to-orange-500 text-white font-semibold rounded-xl text-base md:text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-pink-500/30 transition-all duration-300"
            >
              Ontdek Mogelijkheden
            </motion.button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
